import { SVVInformationMessage } from '@komponentkassen/svv-information-message';
import parse from 'html-react-parser';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useNavigate } from 'react-router';
import { Stegveiviser } from '../../components/Stegveiviser';
import { useApptekster } from '../../contexts/ApptekstContext';
import { hentTrafikkstasjoner, Trafikkstasjon } from '../../utils/api';
import { useApi } from '../../hooks/useApi';
import { SVVSpinner } from '@komponentkassen/svv-spinner';
import { SVVBreadcrumbLink } from '@komponentkassen/svv-breadcrumbs';
import {
    skiltbestillingsteg,
    useSkiltbestillingState,
} from './Skiltbestilling';
import { SVVLayoutBanner } from '@komponentkassen/svv-layout';
import { SVVSelect } from '@komponentkassen/svv-form';
import {
    SVVButton,
    SVVButtonCancel,
    SVVButtonRow,
} from '@komponentkassen/svv-button';

export const VelgHentested: React.FC = () => {
    const t = useApptekster();
    const navigate = useNavigate();
    const { hent, laster, data } = useApi(hentTrafikkstasjoner, {
        id: 'trafikkstasjoner',
    });
    const { handleSubmit } = useForm<{
        fylke: string;
        trafikkstasjon: string;
    }>();

    const kjennemerkestorrelser = useSkiltbestillingState(
        (state) => state.kjennemerkestorrelser,
    );
    const setTrafikkstasjon = useSkiltbestillingState(
        (state) => state.setTrafikkstasjon,
    );
    const trafikkstasjon = useSkiltbestillingState(
        (state) => state.trafikkstasjon,
    );
    const rettighetId = useSkiltbestillingState((state) => state.rettighetId);
    const [valgtFylke, setValgtFylke] = useState(trafikkstasjon?.fylke || '');
    const [valgtTrafikkstasjon, setValgtTrafikkstasjon] = useState(
        trafikkstasjon?.navn || '',
    );
    const [feilmeldingFylke, setFeilmeldingFylke] = useState('');
    const [feilmeldingTrafikkstasjon, setFeilmeldingTrafikkstasjon] =
        useState('');
    const [fylker, setFylker] = useState<string[]>([]);
    const [fylkesTrafikkstasjoner, setFylkesTrafikkstasjoner] = useState<
        Trafikkstasjon[]
    >([]);

    useEffect(() => {
        hent();
    }, [hent]);

    useEffect(() => {
        if (data) {
            setFylker(
                Array.from(
                    new Set(
                        data.trafikkstasjoner
                            .filter(
                                (enTrafikkstasjon) =>
                                    enTrafikkstasjon.tilbyr.skilttjenester,
                            )
                            .map((enTrafikkstasjon) => enTrafikkstasjon.fylke)
                            .sort((a, b) => a.localeCompare(b)),
                    ),
                ),
            );
        }
    }, [data]);

    useEffect(() => {
        if (valgtFylke && data) {
            setFylkesTrafikkstasjoner(
                Array.from(
                    new Set(
                        data.trafikkstasjoner
                            .filter(
                                (enTrafikkstasjon) =>
                                    enTrafikkstasjon.tilbyr.skilttjenester &&
                                    enTrafikkstasjon.fylke === valgtFylke,
                            )
                            .sort(sammenlignTrafikkstasjoner),
                    ),
                ),
            );
        }
    }, [valgtFylke, data]);

    if (!kjennemerkestorrelser.foran && !kjennemerkestorrelser.bak) {
        return <Navigate to="/" />;
    }

    return (
        <>
            <SVVLayoutBanner>
                <SVVBreadcrumbLink
                    href="#/statusoversikt"
                    ariaLabel={t('personlig-kjennemerke.snarvei.status')}
                    className="intern-link"
                >
                    {t('personlig-kjennemerke.snarvei.status')}
                </SVVBreadcrumbLink>
                <h1>{t('personlig-kjennemerke.bestill.overskrift')}</h1>
            </SVVLayoutBanner>

            <SVVLayoutBanner secondary>
                <form
                    noValidate
                    onSubmit={handleSubmit(() => {
                        if (!valgtFylke) {
                            setFeilmeldingFylke(
                                t(
                                    'personlig-kjennemerke.bestill.hentested.fylke.validering',
                                ),
                            );
                        } else if (!valgtTrafikkstasjon) {
                            setFeilmeldingTrafikkstasjon(
                                t(
                                    'personlig-kjennemerke.bestill.hentested.trafikkstasjon.validering',
                                ),
                            );
                        } else {
                            setTrafikkstasjon(
                                tilValgtTrafikkstasjon(
                                    valgtTrafikkstasjon,
                                    fylkesTrafikkstasjoner,
                                ),
                            );
                            navigate('/skiltbestilling/varsling');
                        }
                    })}
                >
                    <div className="form-layout">
                        <div className="form-layout__nav">
                            <Stegveiviser
                                steg={skiltbestillingsteg}
                                aktivtSteg={3}
                            />
                        </div>
                        <div className="form-layout__main">
                            <h2 className="spacing-bottom--xsmall">
                                {t(
                                    'personlig-kjennemerke.bestill.steg.hentested',
                                )}
                            </h2>
                            <p className="spacing-bottom--small">
                                {t(
                                    'personlig-kjennemerke.bestill.hentested.ingress',
                                )}
                            </p>

                            <div aria-live="polite">
                                {laster && (
                                    <div className="spacing-bottom--medium">
                                        <SVVSpinner
                                            size="small"
                                            centered={false}
                                        >
                                            {t(
                                                'personlig-kjennemerke.delt.laster',
                                            )}
                                        </SVVSpinner>
                                    </div>
                                )}
                            </div>
                            <div className="responsive-spacing-top--small">
                                <SVVSelect
                                    id="fylke"
                                    legend={t(
                                        'personlig-kjennemerke.bestill.hentested.fylke.label',
                                    )}
                                    emptyChoiceText={t(
                                        'personlig-kjennemerke.bestill.hentested.velg.fylke',
                                    )}
                                    errorMessage={feilmeldingFylke}
                                    selected={valgtFylke}
                                    options={fylker.map((fylke) => ({
                                        value: fylke,
                                        text: fylke,
                                        disabled: false,
                                    }))}
                                    onChange={(
                                        e: ChangeEvent<HTMLSelectElement>,
                                    ) => {
                                        setValgtTrafikkstasjon('');
                                        setFeilmeldingFylke('');
                                        setValgtFylke(e.target.value);
                                    }}
                                />
                            </div>
                            <div className="responsive-spacing-top--xsmall">
                                {valgtFylke && (
                                    <SVVSelect
                                        id="trafikkstasjon"
                                        legend={t(
                                            'personlig-kjennemerke.bestill.hentested.fylke.label',
                                        )}
                                        emptyChoiceText={t(
                                            'personlig-kjennemerke.bestill.hentested.velg.trafikkstasjon',
                                        )}
                                        selected={valgtTrafikkstasjon}
                                        errorMessage={feilmeldingTrafikkstasjon}
                                        options={fylkesTrafikkstasjoner.map(
                                            (enTrafikkstasjon) => ({
                                                value: enTrafikkstasjon.navn,
                                                text: enTrafikkstasjon.navn,
                                                disabled: false,
                                            }),
                                        )}
                                        onChange={(
                                            e: ChangeEvent<HTMLSelectElement>,
                                        ) => {
                                            setValgtTrafikkstasjon(
                                                e.target.value,
                                            );
                                            setFeilmeldingTrafikkstasjon('');
                                        }}
                                    />
                                )}
                            </div>
                            <div className="responsive-spacing-top--xsmall responsive-spacing-bottom--small">
                                {valgtTrafikkstasjon && (
                                    <>
                                        <SVVInformationMessage type="warning">
                                            {parse(
                                                t(
                                                    'personlig-kjennemerke.erstatningskjennemerke.steg.levering.apningstider.infoboks',
                                                ),
                                            )}
                                        </SVVInformationMessage>
                                    </>
                                )}
                            </div>
                            <div className="svv-knapperad--spacing-top">
                                <SVVButtonRow>
                                    <SVVButton type="submit">
                                        {t('personlig-kjennemerke.delt.neste')}
                                    </SVVButton>
                                    <SVVButton
                                        onClick={() => navigate(-1)}
                                        color="secondary"
                                    >
                                        {t(
                                            'personlig-kjennemerke.delt.tilbake',
                                        )}
                                    </SVVButton>
                                    <SVVButtonCancel
                                        href={`#status/${rettighetId}`}
                                    >
                                        {t('personlig-kjennemerke.delt.avbryt')}
                                    </SVVButtonCancel>
                                </SVVButtonRow>
                            </div>
                        </div>
                    </div>
                </form>
            </SVVLayoutBanner>
        </>
    );
};

const tilValgtTrafikkstasjon = (
    valgtTrafikkstasjon: string,
    trafikkstasjoner: Trafikkstasjon[],
) =>
    trafikkstasjoner?.find(
        (trafikkstasjon) => trafikkstasjon.navn === valgtTrafikkstasjon,
    );

export function sammenlignTrafikkstasjoner(
    a: { navn: string },
    b: { navn: string },
) {
    const navnA = a.navn.toUpperCase();
    const navnB = b.navn.toUpperCase();

    let sammenligning = 0;
    if (navnA > navnB) {
        sammenligning = 1;
    } else if (navnA < navnB) {
        sammenligning = -1;
    }
    return sammenligning;
}
